<template>
  <PaypalButton
    class="paypal-express-checkout-button"
    :label="label"
    is-label-before
    :size="size"
    @click="handleCheckoutStart"
  />
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';

import { infoFlyout, closeFlyout } from '~/composables/flyout';
import {
  addToCart,
  type AddToCartItem,
  getAddToCartItemFromOffer,
  handleCartErrors,
} from '~/helpers/add-to-cart';
import { useCartStore } from '~/stores/cart';
import { useOrderStore } from '~/stores/order';

import { PaypalButton } from '@i22team/td-longtail-vue-components';

export type PaypalExpressCheckoutButtonProps = {
  label?: string;
  offer?: Offer;
  size?: 'normal' | 'large';
};

const props = withDefaults(defineProps<PaypalExpressCheckoutButtonProps>(), {
  label: undefined,
  offer: undefined,
  size: 'normal',
});

const cartStore = useCartStore();
const orderStore = useOrderStore();
const { cartItems } = storeToRefs(cartStore);

const emits = defineEmits<{
  (e: 'checkout:success', payload: string): void;
  (e: 'checkout:start'): void;
  (e: 'checkout:failure', payload: Error): void;
}>();

const handleCheckoutSuccess = (redirect: string) => {
  emits('checkout:success', redirect);
  window.location.href = redirect;
  setTimeout(() => {
    closeFlyout();
  }, 5000);
};

const getItem = (articleNumber: string): AddToCartItem | undefined => {
  const item = cartItems?.find((e: any) => e.articleNumber === articleNumber);
  if (item) return item;
  const { offer } = props;
  if (offer?.product && offer.product?.articleNumber === articleNumber) {
    return getAddToCartItemFromOffer(offer);
  }
  return undefined;
};

const handleCheckoutFailure = (error: Error) => {
  handleCartErrors(
    error,
    (articleNumber) => {
      const item = getItem(articleNumber);
      return item?.name || articleNumber;
    },
    'Die Weiterleitung zu PayPal konnte nicht durchgeführt werden.'
  );
  emits('checkout:failure', error);
};

const showPaypalExpressStartDialog = () => {
  infoFlyout('Du wirst zu PayPal weitergeleitet ...', { type: 'info' });
};

const handleCheckoutStart = async () => {
  emits('checkout:start');
  if (props.offer && Object.keys(props.offer).length > 0) {
    if (!(await addToCart([props.offer], showPaypalExpressStartDialog))) {
      return;
    }
  }

  try {
    if ((cartStore.cartItems?.length || 0) === 0) {
      infoFlyout('Es sind keine Produkte im Warenkorb.', { type: 'error' });
      return;
    }

    showPaypalExpressStartDialog();

    const data = await orderStore.submitPaypalExpressOrder({});

    if (!data?.redirect) {
      infoFlyout('Die Weiterleitung zu PayPal konnte nicht durchgeführt werden.', {
        type: 'error',
      });
      return;
    }

    handleCheckoutSuccess(data.redirect);
  } catch (error: Error | any) {
    handleCheckoutFailure(error);
  }
};
</script>
